import { Schema } from 'effect';

import type { ParserDefinitionSpec, ParsedPage } from '../parser-layer';
import type { CanonicalBlock, CanonicalParseResult } from '../types';
import type { ParserUsage } from '../parser';

const optionalMutable = <S extends Schema.Constraint>(schema: S) =>
  Schema.mutableKey(Schema.optionalKey(schema));
const mutableArray = <S extends Schema.Constraint>(schema: S) =>
  Schema.mutable(Schema.Array(schema));

export interface OkraParsePage {
  page: number;
  markdown?: string;
  text?: string;
  html?: string;
  tables?: unknown[];
  elements?: unknown[];
}

/** The sole canonical definition of the parser-Worker output projection. */
export type OkraParseOutput = {
  markdown?: string;
  pages: OkraParsePage[];
  tables?: unknown[];
  anchors?: Array<{
    page: number;
    text?: string;
    label?: string;
    bbox?: { x: number; y: number; w: number; h: number };
    type?: string;
  }>;
  metadata?: Record<string, unknown>;
};

const OkraParsePageSchema = Schema.Struct({
  page: Schema.Finite.pipe(Schema.mutableKey),
  markdown: optionalMutable(Schema.String),
  text: optionalMutable(Schema.String),
  html: optionalMutable(Schema.String),
  tables: optionalMutable(mutableArray(Schema.Unknown)),
  elements: optionalMutable(mutableArray(Schema.Unknown)),
});

export const OkraParseOutputSchema: Schema.Codec<OkraParseOutput> = Schema.Struct({
  markdown: optionalMutable(Schema.String),
  pages: Schema.mutableKey(mutableArray(OkraParsePageSchema)),
  tables: optionalMutable(mutableArray(Schema.Unknown)),
  anchors: optionalMutable(
    mutableArray(
      Schema.Struct({
        page: Schema.Finite.pipe(Schema.mutableKey),
        text: optionalMutable(Schema.String),
        label: optionalMutable(Schema.String),
        bbox: optionalMutable(
          Schema.Struct({
            x: Schema.Finite.pipe(Schema.mutableKey),
            y: Schema.Finite.pipe(Schema.mutableKey),
            w: Schema.Finite.pipe(Schema.mutableKey),
            h: Schema.Finite.pipe(Schema.mutableKey),
          }),
        ),
        type: optionalMutable(Schema.String),
      }),
    ),
  ),
  metadata: optionalMutable(Schema.Record(Schema.String, Schema.Unknown)),
});

function blockText(block: CanonicalBlock): string | undefined {
  return block.value ?? block.label;
}

function pageText(blocks: readonly CanonicalBlock[]): string | undefined {
  const text = blocks.flatMap((block) => {
    const own = blockText(block);
    const children = block.children ? pageText(block.children) : undefined;
    return [own, children].filter((value): value is string => Boolean(value));
  });
  return text.length > 0 ? text.join('\n') : undefined;
}

export interface CanonicalTiming {
  readonly durationMs: number;
  readonly model?: string;
  readonly confidence?: number | null;
}

function sumOptional(values: readonly (number | undefined)[]): number | undefined {
  return values.some((value) => value !== undefined)
    ? values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
    : undefined;
}

function aggregateUsage(pages: readonly ParsedPage[]): ParserUsage | undefined {
  const values = pages.flatMap((page) => (page.usage === undefined ? [] : [page.usage]));
  if (values.length === 0) return undefined;
  const first = values[0]!;
  const inputTokens = sumOptional(values.map((usage) => usage.inputTokens));
  const outputTokens = sumOptional(values.map((usage) => usage.outputTokens));
  const totalTokens = sumOptional(values.map((usage) => usage.totalTokens));
  const costUsd = sumOptional(values.map((usage) => usage.costUsd));
  return {
    ...(first.provider === undefined ? {} : { provider: first.provider }),
    ...(first.model === undefined ? {} : { model: first.model }),
    ...(first.actualModelId === undefined ? {} : { actualModelId: first.actualModelId }),
    ...(first.parserProfile === undefined ? {} : { parserProfile: first.parserProfile }),
    ...(inputTokens === undefined ? {} : { inputTokens }),
    ...(outputTokens === undefined ? {} : { outputTokens }),
    ...(totalTokens === undefined ? {} : { totalTokens }),
    ...(costUsd === undefined ? {} : { costUsd }),
    ...(values.some((usage) => usage.costEstimated !== undefined)
      ? { costEstimated: values.some((usage) => usage.costEstimated === true) }
      : {}),
    ...(first.currency === undefined ? {} : { currency: first.currency }),
  };
}

export function toCanonicalParseResult(
  pages: readonly ParsedPage[],
  spec: ParserDefinitionSpec,
  timing: CanonicalTiming,
): CanonicalParseResult {
  const ordered = [...pages].sort((left, right) => left.page - right.page);
  const usage = aggregateUsage(ordered);
  return {
    pages: ordered.map((page) => ({
      pageNumber: page.page,
      ...(page.width === undefined ? {} : { width: page.width }),
      ...(page.height === undefined ? {} : { height: page.height }),
      blocks: [...page.blocks],
    })),
    ...(ordered.some((page) => page.raw !== undefined)
      ? { rawPages: ordered.map((page) => page.raw) }
      : {}),
    metadata: {
      parser: spec.id,
      ...((timing.model ?? ordered.find((page) => page.usage?.model)?.usage?.model) === undefined
        ? {}
        : { model: timing.model ?? ordered.find((page) => page.usage?.model)?.usage?.model }),
      durationMs: timing.durationMs,
      confidence: timing.confidence ?? null,
      pageCount: ordered.length,
      ...(usage === undefined ? {} : { usage }),
    },
  };
}

export function toOkraParseOutput(result: CanonicalParseResult): OkraParseOutput {
  const pages = result.pages.map((page, index) => {
    const text = pageText(page.blocks);
    const tables = page.blocks.filter((block) => block.type === 'table');
    const raw = result.rawPages?.[index];
    const rawPage =
      typeof raw === 'object' && raw !== null && 'pageOutput' in raw
        ? (raw as { readonly pageOutput?: unknown }).pageOutput
        : undefined;
    if (typeof rawPage === 'object' && rawPage !== null) {
      return { ...(rawPage as OkraParsePage) } satisfies OkraParsePage;
    }
    return {
      page: page.pageNumber,
      ...(text === undefined ? {} : { text, markdown: text }),
      ...(tables.length === 0 ? {} : { tables }),
      elements: [...page.blocks],
    } satisfies OkraParsePage;
  });
  const tables = pages.flatMap((page) => page.tables ?? []);
  const anchors = result.pages.flatMap((page) =>
    page.blocks.flatMap((block) =>
      block.bbox
        ? [
            {
              page: page.pageNumber,
              ...(blockText(block) === undefined ? {} : { text: blockText(block) }),
              ...(block.label === undefined ? {} : { label: block.label }),
              bbox: { ...block.bbox },
              type: block.type,
            },
          ]
        : [],
    ),
  );
  const rawDocument = result.rawPages?.find(
    (raw) => typeof raw === 'object' && raw !== null && 'documentOutput' in raw,
  ) as { readonly documentOutput?: Partial<OkraParseOutput> } | undefined;
  const markdown = rawDocument
    ? rawDocument.documentOutput?.markdown
    : pages
        .map((page) => page.markdown)
        .filter((value): value is string => value !== undefined)
        .join('\n\n');
  if (rawDocument) {
    return {
      ...(markdown === undefined ? {} : { markdown }),
      pages,
      ...(rawDocument.documentOutput?.tables === undefined
        ? {}
        : { tables: rawDocument.documentOutput.tables }),
      anchors: rawDocument.documentOutput?.anchors ?? [],
      ...(rawDocument.documentOutput?.metadata === undefined
        ? {}
        : { metadata: rawDocument.documentOutput.metadata }),
    };
  }
  return {
    ...(markdown ? { markdown } : {}),
    pages,
    ...(tables.length === 0 ? {} : { tables }),
    ...(anchors.length === 0 ? {} : { anchors }),
    metadata: { ...result.metadata },
  };
}
